import { useCallback, useEffect, useState } from "react";
import { v4Service, PaymentType } from "src/api";
import { useUrlParamsContext } from "src/contexts";
import { debounce } from "src/utils";

interface UseEstimatePaymentFeeResult {
  paymentFee: number;
  paymentFeeLoading: boolean;
  paymentFeeError: string;
}

interface UseEstimatePaymentFeeProps {
  paymentType: PaymentType;
  amount: number;
}

export const useEstimatePaymentFee = ({ paymentType, amount }: UseEstimatePaymentFeeProps): UseEstimatePaymentFeeResult => {
  const [paymentFee, setPaymentFee] = useState<number>(0);
  const [paymentFeeLoading, setPaymentFeeLoading] = useState(false);
  const [paymentFeeError, setPaymentFeeError] = useState<string>("");
  const { paymentRouteId } = useUrlParamsContext();

  const getPaymentFee = useCallback(
    debounce(300, async (amount: number) => {
      return await v4Service.estimatePaymentFee({
        payment_route_id: paymentRouteId,
        type: paymentType,
        amount,
      });
    }),
    [paymentRouteId, paymentType]
  );

  useEffect(() => {
    if (amount <= 0) {
      setPaymentFee(0);
      setPaymentFeeError("");
      setPaymentFeeLoading(false);
      return;
    }

    const estimatePaymentFee = async () => {
      if (amount && paymentRouteId && paymentType) {
        try {
          setPaymentFeeLoading(true);
          const paymentFee = await getPaymentFee(amount);
          setPaymentFee(paymentFee);
        } catch (error) {
          console.error("Error estimating payment fee:", error);
          setPaymentFeeError(String(error));
          setPaymentFee(0);
        } finally {
          setPaymentFeeLoading(false);
        }
      }
    };

    estimatePaymentFee();
  }, [amount, paymentType, paymentRouteId, getPaymentFee]);

  return {
    paymentFee: amount > 0 ? paymentFee : 0,
    paymentFeeLoading,
    paymentFeeError,
  };
};
